import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowUpRight, Truck, Wrench, Check, Star, Phone } from "lucide-react";
import { api, formatPriceRange } from "@/lib/flow";
import ReviewsSection from "@/components/site/ReviewsSection";
import ShowcaseCarousel from "@/components/site/ShowcaseCarousel";
import SocialIcons from "@/components/site/SocialIcons";
import SEO from "@/components/site/SEO";
import AutoPlayVideo from "@/components/site/AutoPlayVideo";

const HERO_IMG =
    "/img/optim/k6eo93my-hero-900.webp";
const FEATURE_IMG =
    "/img/optim/wtpyvmyn_ChatGPT_20Image_20May_209_2C_202026_2C_2005_11_05_2.webp";
const DELIVERY_IMG =
    "https://images.unsplash.com/photo-1636317269665-813f3fc4978e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxkZWxpdmVyeSUyMGluc3RhbGxhdGlvbiUyMHByb2Zlc3Npb25hbHxlbnwwfHx8fDE3NzgyMDE4ODR8MA&ixlib=rb-4.1.0&q=85";

export default function Home() {
    const [featured, setFeatured] = useState([]);

    useEffect(() => {
        api.get("/products").then((r) => {
            const all = r.data || [];
            const picks = [
                all.find((p) => p.id === "flowluxnova"),
                all.find((p) => p.id === "flowluxeclipse"),
                all.find((p) => p.id === "flowluxlunar"),
            ].filter(Boolean);
            setFeatured(picks.length ? picks : all.slice(0, 3));
        }).catch(() => {});
    }, []);

    return (
        <div data-testid="page-home">
            <SEO
                title="FlowLux — Luxury Smart Toilets · Miami, Fort Lauderdale, Boca Raton & South Florida"
                description="Luxury smart toilets and bidet toilets delivered and installed across South Florida — Miami, Fort Lauderdale, Boca Raton, West Palm Beach, Hollywood, Coral Gables, Aventura, Doral. Heated seats, auto open/close, warm water wash, self-cleaning. Free shipping, money-back guarantee."
                keywords="smart toilet Miami, smart toilet Fort Lauderdale, smart toilet Boca Raton, smart toilet West Palm Beach, smart toilet Broward County, luxury smart toilet South Florida, bidet toilet Miami, japanese toilet Miami, intelligent toilet South Florida, smart toilet with heated seat, smart toilet with bidet, smart toilet with remote control, smart toilet auto flush, smart toilet auto open close, one piece smart toilet, wall hung smart toilet, black smart toilet, smart toilet with dryer, smart toilet with night light, smart toilet with warm water wash, self cleaning smart toilet, smart toilet with foot sensor, smart toilet installation Miami, smart toilet delivery and installation, buy smart toilet South Florida, smart toilet showroom Florida, best smart toilet 2026"
                canonical="/"
            />
            {/* Hero */}
            <section className="relative">
                <div className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-16 md:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-6 fl-fade-up">
                        <div className="fl-eyebrow">FlowLux</div>
                        <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-flow-ink"
                            style={{ fontWeight: 200 }}>
                            For Those Who Demand <span className="text-flow-blue" style={{ fontWeight: 200 }}>Luxury</span>
                        </h1>
                        <p className="mt-8 text-base md:text-lg text-flow-muted max-w-lg leading-relaxed">
                            South Florida&apos;s premier luxury smart toilet experience, delivered across the United States
                        </p>

                        <div className="mt-10 flex flex-wrap items-center gap-4">
                            <Link to="/products" className="fl-btn-primary text-sm" data-testid="hero-cta-explore">
                                Explore the Catalog <ArrowUpRight size={16} />
                            </Link>
                            <Link to="/quote" className="fl-btn-ghost text-sm" data-testid="hero-cta-quote">
                                Contact
                            </Link>
                            <a href="tel:+15613360491" className="fl-btn-ghost text-sm inline-flex items-center gap-2" data-testid="hero-cta-call">
                                <Phone size={14} strokeWidth={1.6} />
                                Call Now
                            </a>
                        </div>

                        <div className="mt-14 flex flex-wrap gap-x-10 gap-y-6 max-w-md">
                            {[
                                ["900+", "Clients"],
                                ["7+", "Years"],
                                ["250+", "5 Star Deliveries"],
                            ].map(([a, b]) => (
                                <div key={b}>
                                    <div className="text-3xl text-flow-ink" style={{ fontWeight: 200 }}>{a}</div>
                                    <div className="text-[10px] tracking-[0.25em] uppercase text-flow-muted mt-1">{b}</div>
                                </div>
                            ))}
                        </div>

                        <ul className="mt-10 space-y-3 max-w-lg" data-testid="hero-trust-list">
                            {[
                                "Trusted by 58 Businesses and 15 Hotels in the US",
                                "Licensed and Insured Installation Professionals",
                                "Free FlowLux Warranty on Every Toilet",
                                "Founded in 2019",
                            ].map((item) => (
                                <li key={item} className="flex items-start gap-3 text-sm text-flow-ink">
                                    <Check size={16} className="text-flow-blue mt-[3px] shrink-0" strokeWidth={1.5} />
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>

                        <a
                            href="#flowlux-reviews"
                            onClick={(e) => {
                                e.preventDefault();
                                document.getElementById("flowlux-reviews")?.scrollIntoView({ behavior: "smooth", block: "start" });
                            }}
                            className="mt-8 inline-flex items-center gap-3 hover:opacity-80 transition-opacity cursor-pointer focus:outline-none"
                            data-testid="hero-google-reviews"
                        >
                            <div className="flex items-center gap-[2px]">
                                {[0,1,2,3,4].map((i) => (
                                    <Star key={i} size={14} className="text-[#FBBC05]" fill="#FBBC05" strokeWidth={0} />
                                ))}
                            </div>
                            <span className="text-sm text-flow-ink underline-offset-4 hover:underline">250+ 5 Star Reviews</span>
                        </a>

                        <div className="mt-4" data-testid="hero-social">
                            <SocialIcons size={18} testIdPrefix="hero-social" showRating={false} />
                        </div>
                    </div>

                    <div className="lg:col-span-6 fl-fade-up delay-2">
                        <div className="relative">
                            <div className="absolute -inset-3 bg-flow-soft" aria-hidden="true" />
                            <img
                                src={HERO_IMG}
                                alt="Minimalist luxury bathroom"
                                width="900"
                                height="900"
                                fetchPriority="high"
                                decoding="async"
                                className="relative w-full h-[460px] md:h-[600px] object-cover"
                            />
                            <div className="absolute bottom-6 left-6 bg-white px-5 py-4 border border-flow-line max-w-[260px]">
                                <div className="fl-eyebrow">Included</div>
                                <div className="text-sm mt-1 text-flow-ink">
                                    Delivery + Installation + Warranty
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* 3 product videos — side by side, luxurious */}
            <section className="border-t border-flow-line bg-flow-soft" data-testid="section-hero-videos">
                <div className="max-w-7xl mx-auto px-6 md:px-10 py-16">
                    <div className="text-center mb-10">
                        <h2 className="text-3xl md:text-4xl lg:text-5xl tracking-tight text-flow-ink" style={{ fontWeight: 200 }}>
                            South Florida&apos;s Premier Luxury Smart Toilets
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        {[
                            "/video/nova.mp4",
                            "/video/pulsar.mp4",
                            "/video/feature.mp4",
                        ].map((src, i) => (
                            <div
                                key={i}
                                className="aspect-[3/4] bg-white overflow-hidden border border-flow-line shadow-[0_30px_80px_-20px_rgba(30,91,184,0.25)]"
                                data-testid={`hero-video-${i}`}
                            >
                                <AutoPlayVideo
                                    src={src}
                                    className="w-full h-full object-cover"
                                    testId={`hero-video-el-${i}`}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Process */}
            <section className="border-t border-flow-line bg-white" data-testid="section-process">
                <div className="max-w-7xl mx-auto px-6 md:px-10 py-24">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
                        <div className="md:col-span-4 text-left md:flex md:flex-col md:justify-center" data-reveal>
                            <div className="fl-eyebrow">The Process</div>
                            <h2 className="mt-4 text-3xl md:text-4xl tracking-normal text-flow-ink"
                                style={{ fontWeight: 200 }}>
                                Three quick steps to a luxury bathroom
                            </h2>
                            <p className="mt-6 text-flow-muted text-sm leading-relaxed max-w-sm">
                                We handle everything — sourcing, delivery and installation — so
                                you only have to enjoy the luxury
                            </p>
                        </div>

                        <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-3 gap-px bg-flow-line border border-flow-line" data-reveal data-reveal-delay="2">
                            {[
                                {
                                    n: "01",
                                    t: "Choose",
                                    d: "Browse the catalog and buy the model that suits your space",
                                    svg: (
                                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" className="text-flow-ink">
                                            <path d="M5 4h14v8a4 4 0 0 1-4 4H9a4 4 0 0 1-4-4V4z" />
                                            <path d="M3 4h18" />
                                            <path d="M9 16l-1 4" />
                                            <path d="M15 16l1 4" />
                                            <path d="M9 9h6" />
                                        </svg>
                                    ),
                                    img: "/img/steps/choose",
                                },
                                {
                                    n: "02",
                                    t: "We Deliver",
                                    d: "Once we deliver your toilet, book an installation appointment with our team",
                                    icon: Truck,
                                    img: "/img/steps/deliver",
                                },
                                {
                                    n: "03",
                                    t: "We Install",
                                    d: "Our team installs everything — including the outlet if needed",
                                    icon: Wrench,
                                    img: "/img/steps/install",
                                },
                            ].map((step) => {
                                const Icon = step.icon;
                                return (
                                    <div key={step.n} className="bg-white flex flex-col items-start hover:bg-flow-surface transition-colors">
                                        <div className="p-8 w-full">
                                            <div className="text-flow-blue text-sm tracking-[0.25em]">{step.n}</div>
                                            {step.svg ? (
                                                <div className="mt-6">{step.svg}</div>
                                            ) : (
                                                <Icon size={22} className="mt-6 text-flow-ink" strokeWidth={1} />
                                            )}
                                            <div className="mt-6 text-xl text-flow-ink" style={{ fontWeight: 200 }}>{step.t}</div>
                                            <div className="mt-3 text-sm text-flow-muted leading-relaxed">{step.d}</div>
                                        </div>
                                        <div className="px-8 pb-8 w-full flex justify-center mt-auto">
                                            <div className="w-36 h-36 md:w-40 md:h-40 rounded-full bg-flow-surface overflow-hidden border border-flow-line">
                                                {step.img ? (
                                                    <picture>
                                                        <source
                                                            type="image/webp"
                                                            srcSet={`${step.img}-320.webp 1x, ${step.img}-480.webp 2x`}
                                                        />
                                                        <img
                                                            src={`${step.img}-320.jpg`}
                                                            srcSet={`${step.img}-320.jpg 1x, ${step.img}-480.jpg 2x`}
                                                            alt={step.t}
                                                            width="320"
                                                            height="320"
                                                            loading="lazy"
                                                            decoding="async"
                                                            className="w-full h-full object-cover"
                                                        />
                                                    </picture>
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center text-[8px] tracking-[0.2em] uppercase text-flow-muted text-center px-2">
                                                        Photo<br />coming soon
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </section>

            {/* Product showcase carousel */}
            <ShowcaseCarousel />

            {/* Price Ladder — Luxury at Every Price */}
            <section className="border-t border-flow-line bg-flow-surface" data-testid="section-price-ladder">
                <div className="max-w-7xl mx-auto px-6 md:px-10 py-24">
                    <div className="text-left md:text-center max-w-5xl mx-auto" data-reveal>
                        <h2 className="text-3xl md:text-4xl lg:text-5xl tracking-tight text-flow-ink md:whitespace-nowrap"
                            style={{ fontWeight: 200 }}>
                            FlowLux Provides Luxury at Every Price
                        </h2>
                        <p className="mt-6 text-flow-muted leading-relaxed">
                            Whether you&apos;re looking for simplicity or ultimate luxury, our curated collection offers the perfect solution for every space and budget
                        </p>
                    </div>

                    {(() => {
                        const ladder = [
                            { name: "Pulsar", price: 570, img: "/img/optim/098owm5h_765e8285-4cd3-45c5-8e89-21c8f5e51f44__1_-removebg-p-thumb.webp" },
                            { name: "Nova", price: 680, img: "/img/optim/bu0yk1bk_53dbb24c-806a-47e8-853d-5fe46eacd0b8-thumb.webp" },
                            { name: "Halo", price: 745, img: "/img/optim/st82gm5i_ecba0e2d-8455-4f0b-85d8-a8a853f0037c-thumb.webp" },
                            { name: "Lunar", price: 890, img: "/img/optim/aidhhbh0_00a0b039-db80-412b-9ffa-afb008798243-thumb.webp" },
                            { name: "Eclipse", price: 980, img: "/img/optim/8t1d6jpu_e54a8a6b-c024-4cfa-a52c-0e0ead44318a_20_281_29-thumb.webp" },
                            { name: "Nebula", price: 2750, img: "/img/optim/s0s2v7s8_ChatGPT_20Image_20May_2027_2C_202026_2C_2004_58_04-thumb.webp" },
                            { name: "Solaris", price: 3900, img: "/img/optim/h35ni9z1_c760f30f-3556-49ff-90fe-b98864c2fa34-removebg-previ-thumb.webp" },
                        ];
                        return (
                            <div className="mt-16 md:mt-24" data-testid="price-ladder">
                                {/* Desktop: horizontal timeline */}
                                <div className="hidden md:block relative">
                                    {/* connecting line — sits across the dot row */}
                                    <div className="absolute left-0 right-0 top-[152px] h-px bg-flow-line" aria-hidden="true" />
                                    <div className="relative grid grid-cols-7 gap-2 items-start">
                                        {ladder.map((item) => (
                                            <Link
                                                key={item.name}
                                                to="/products"
                                                className="flex flex-col items-center text-center group focus:outline-none"
                                                data-testid={`ladder-${item.name.toLowerCase()}`}
                                            >
                                                <div className="w-[120px] h-[120px] flex items-center justify-center mix-blend-multiply transition-transform duration-300 group-hover:scale-[1.04]">
                                                    <img src={item.img} alt={`The FlowLux ${item.name}`} width="240" height="240" loading="lazy" decoding="async" className="max-w-full max-h-full object-contain" />
                                                </div>
                                                <div className="relative z-10 mt-4 w-3 h-3 rounded-full bg-flow-blue ring-4 ring-flow-surface" />
                                                <div className="mt-4 text-[10px] tracking-[0.25em] uppercase text-flow-muted">The FlowLux</div>
                                                <div className="mt-1 text-lg text-flow-ink group-hover:text-flow-blue transition-colors" style={{ fontWeight: 200 }}>
                                                    {item.name}
                                                </div>
                                                <div className="mt-1 text-flow-blue text-sm">${item.price.toLocaleString()}</div>
                                            </Link>
                                        ))}
                                    </div>
                                </div>

                                {/* Mobile: vertical timeline */}
                                <div className="md:hidden relative pl-8">
                                    <div className="absolute left-3 top-2 bottom-2 w-px bg-flow-line" aria-hidden="true" />
                                    <div className="space-y-8">
                                        {ladder.map((item) => (
                                            <Link
                                                key={item.name}
                                                to="/products"
                                                className="relative flex items-center gap-4 group"
                                                data-testid={`ladder-mobile-${item.name.toLowerCase()}`}
                                            >
                                                <div className="absolute -left-[22px] w-3 h-3 rounded-full bg-flow-blue ring-4 ring-flow-surface" />
                                                <div className="w-20 h-20 flex-shrink-0 flex items-center justify-center mix-blend-multiply">
                                                    <img src={item.img} alt={`The FlowLux ${item.name}`} width="240" height="240" loading="lazy" decoding="async" className="max-w-full max-h-full object-contain" />
                                                </div>
                                                <div>
                                                    <div className="text-[10px] tracking-[0.25em] uppercase text-flow-muted">The FlowLux</div>
                                                    <div className="mt-1 text-lg text-flow-ink group-hover:text-flow-blue transition-colors" style={{ fontWeight: 200 }}>
                                                        {item.name}
                                                    </div>
                                                    <div className="mt-1 text-flow-blue text-sm">${item.price.toLocaleString()}</div>
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        );
                    })()}
                </div>
            </section>

            {/* Featured */}
            <section className="bg-white border-t border-flow-line" data-testid="section-featured">
                <div className="max-w-7xl mx-auto px-6 md:px-10 py-24">
                    <div className="flex items-end justify-between flex-wrap gap-6" data-reveal>
                        <div>
                            <div className="fl-eyebrow">Featured</div>
                            <h2 className="mt-3 text-3xl md:text-4xl tracking-tight text-flow-ink"
                                style={{ fontWeight: 200 }}>
                                A curated selection
                            </h2>
                        </div>
                        <Link to="/products" className="fl-link text-sm" data-testid="featured-view-all">
                            View entire catalog →
                        </Link>
                    </div>

                    <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-px bg-flow-line border border-flow-line" data-reveal data-reveal-delay="2">
                        {featured.map((p) => (
                            <Link
                                key={p.id}
                                to={`/products/${p.id}`}
                                className="bg-white group block"
                                data-testid={`featured-card-${p.id}`}
                            >
                                <div className="aspect-[4/5] overflow-hidden bg-flow-surface">
                                    <img
                                        src={p.image_url}
                                        alt={p.short_name}
                                        width="800"
                                        height="1000"
                                        className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700"
                                        loading="lazy"
                                        decoding="async"
                                    />
                                </div>
                                <div className="p-6">
                                    <div className="flex items-center justify-between">
                                        <div className="fl-eyebrow">{p.badge || "FlowLux"}</div>
                                        <ArrowUpRight size={16} className="text-flow-muted group-hover:text-flow-blue transition-colors" />
                                    </div>
                                    <div className="mt-3 text-lg text-flow-ink" style={{ fontWeight: 200 }}>
                                        {p.short_name}
                                    </div>
                                    <div className="mt-2 text-sm text-flow-muted">
                                        {formatPriceRange(p.price_min, p.price_max)} · install +{formatPriceRange(p.install_min, p.install_max)}
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* FlowLux Reviews */}
            <ReviewsSection />

            {/* Repair services */}
            <section className="bg-white border-t border-flow-line" data-testid="section-repair">
                <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-6" data-reveal>
                        <h2 className="mt-0 text-3xl md:text-4xl tracking-normal text-flow-ink"
                            style={{ fontWeight: 200 }}>
                            FlowLux Toilet Repair Services
                        </h2>
                        <p className="mt-5 text-sm md:text-base text-flow-muted leading-relaxed max-w-lg">
                            Book the number 1 ranked toilet repair professionals in South Florida
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link to="/repair" className="fl-btn-primary text-sm" data-testid="repair-cta-book">
                                Book Repair Visit
                            </Link>
                            <Link to="/products" className="fl-btn-ghost text-sm" data-testid="repair-cta-shop">
                                Browse Catalog
                            </Link>
                        </div>

                        <div className="mt-6 flex items-center gap-3" data-testid="repair-reviews">
                            <div className="flex items-center gap-[2px]">
                                {[0,1,2,3,4].map((i) => (
                                    <Star key={i} size={14} className="text-[#FBBC05]" fill="#FBBC05" strokeWidth={0} />
                                ))}
                            </div>
                            <span className="text-sm text-flow-ink">250+ 5 Star Reviews</span>
                        </div>
                    </div>
                    <div className="lg:col-span-6 flex justify-center">
                        <div className="w-[320px] h-[320px] md:w-[420px] md:h-[420px] rounded-full overflow-hidden border border-flow-line bg-flow-surface shadow-[0_30px_80px_-20px_rgba(30,91,184,0.25)]">
                            <AutoPlayVideo
                                src="/video/feature.mp4"
                                ariaLabel="FlowLux service van"
                                testId="repair-section-video"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Editorial split */}
            <section className="bg-flow-surface" data-testid="section-editorial">
                <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-6 flex justify-center" data-reveal>
                        <div className="w-[320px] h-[320px] md:w-[460px] md:h-[460px] rounded-full overflow-hidden border border-flow-line bg-flow-surface">
                            <img src={FEATURE_IMG} alt="Luxury bathroom" width="920" height="920" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                        </div>
                    </div>
                    <div className="lg:col-span-6" data-reveal data-reveal-delay="2">
                        <div className="fl-eyebrow">The FlowLux Promise</div>
                        <h2 className="mt-4 text-3xl md:text-4xl tracking-tight text-flow-ink"
                            style={{ fontWeight: 200 }}>
                            Guaranteed Quality and Luxury <br />Without the Hassle
                        </h2>
                        <p className="mt-6 text-flow-muted leading-relaxed max-w-md">
                            FlowLux brings the standard of five star living into your most private space. Heated seating, whisper quiet auto lift, warm air drying, and endless customization designed for those who demand the finest in every detail of their lives
                        </p>
                        <div className="mt-10 grid grid-cols-2 gap-8 max-w-md">
                            {[
                                ["Customized", "Choose the model and finishes that fit you"],
                                ["Delivered", "Anywhere in South Florida — we come to you"],
                                ["Installed", "Plumbing, outlets, set-up — handled"],
                                ["FlowLux Warranty", "Every FlowLux toilet has a free warranty included"],
                            ].map(([t, d]) => (
                                <div key={t}>
                                    <div className="text-flow-blue text-sm tracking-[0.18em] uppercase">{t}</div>
                                    <div className="mt-2 text-sm text-flow-muted leading-relaxed">{d}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Delivery banner */}
            <section className="bg-white border-t border-flow-line" data-testid="section-cta">
                <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20">
                    <div className="max-w-2xl mx-auto text-left md:text-center" data-reveal>
                        <div className="fl-eyebrow">Ready to upgrade?</div>
                        <h2 className="mt-4 text-3xl md:text-5xl tracking-tight text-flow-ink" style={{ fontWeight: 200 }}>
                            Tell us about your bathroom <br className="hidden md:block" />We&apos;ll handle the rest
                        </h2>
                        <div className="mt-8 flex flex-wrap gap-4 justify-start md:justify-center">
                            <Link to="/quote" className="fl-btn-primary text-sm" data-testid="cta-banner-quote">
                                Contact
                            </Link>
                            <Link to="/products" className="fl-btn-ghost text-sm" data-testid="cta-banner-book">
                                Explore the Catalog
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
